import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';
import Projection from 'ol/proj/Projection';

import type { LayerState, VectorLayerState } from '@tmapy/beetlejuice-utils';
import { QUERY_STATE } from '@tmapy/beetlejuice-utils';
import { useSelector } from '@tmapy/redux';
import { useMessage } from '@tmapy/intl';
import { BtnBar, BtnBarSpan, Checkbox, CtaBtn, DangerAlert, FormItem, InfoAlert, Link, SecondaryBtn, StickyFooter, SvgArrowLeft, WaitingAlert } from '@tmapy/style-guide';

import type { OrderEditAttachmentRequest, OrderEditRequest, OrderPricePreviewResponse } from '../../openapi/types';
import { OrderRegionTypesEnum } from '../../openapi/types/OrderRegionTypesEnum';
import { usePublishsetsListResource } from '../../openapi/operations/publishsetsList';
import { OrderTypeEnum } from '../../openapi/types/OrderTypeEnum';
import { OrderSelectionTypesEnum } from '../../openapi/types/OrderSelectionTypesEnum';
import { ordersCreate } from '../../openapi/operations/ordersCreate';
import { ordersPartialUpdate } from '../../openapi/operations/ordersPartialUpdate';
import { ordersPricePreviewCreate } from '../../openapi/operations/ordersPricePreviewCreate';

import type { OmittedFormItemProps } from '../../types';
import { UNMANAGED_AREA_LAYERS } from '../../areas/constants';
import { useAuth, useFormItemServerErrors, useHistoryAction } from '../../hooks';
import { useCheckbox } from '../../hooks/useCheckbox';
import { actionSetAgreement, actionSetRequirements, submitOrder } from '../actions/newOrder';

import { NEW_ORDER_STEPS } from '../constants';
import { canSetCounterDetails } from '../utils/canSetCounterDetails';
import { isSinglePolygonArea } from '../utils/isSinglePolygonArea';

import { AreasSummary } from './AreasSummary';
import { createHandlingFeeRows, PriceSummary } from './PriceSummary';
import type { PriceSummaryRow } from './PriceSummary';
import { PublishsetSummaryItem } from './PublishsetSummaryItem';
import { newOrderMessages, orderPriceMessages, originMessages } from './orderMessages';
import { ORDER_IS_PUBLIC } from '../../constants';

type ExportGeometry = {
  type: string;
  crs: {
    type: string;
    properties: {
      [id: string]: Projection | undefined | string;
    };
  };
  coordinates: number[];
};

type SummaryProps = {
  onStepChange(newStep: NEW_ORDER_STEPS): void;
  onPrevStepClick(): void;
};

const isDateValid = (
  date: Date | null,
  dateValidityMin: Date | null,
  dateValidityMax: Date | null,
): boolean => {
  if (date && (dateValidityMin || dateValidityMax)) {
    return date >= (dateValidityMin || date) && date <= (dateValidityMax || date);
  }
  return true;
};

export const Summary: React.FC<SummaryProps> = ({ onPrevStepClick, onStepChange }) => {
  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const config = useSelector((state) => state.app.config.vydej) ?? {};
  const projection = useSelector((state) => state.mapCore.view.projection);
  const {
    order_type,
    order_data_state_valid_at,
    order_data_changes_from,
    selection_type,
    is_public,
    from_primary_db,
    handling_fee,
  } = useSelector((state) => state.newOrder);

  const isOrderDataStateValid = !!order_data_state_valid_at?.toJSON();
  const isOrderDataChangesFrom = !!order_data_changes_from?.toJSON();

  const customerType = useSelector((state) => state.app.config.customerType);
  const auth = useAuth();
  const isCounterSectionVisible = canSetCounterDetails(customerType, auth.hasPermission);
  const publishsetList = usePublishsetsListResource(
    { state: 'PUBLISHED', page_size: 100000, order_type },
    true,
  );

  const newOrder = useSelector((state) => state.newOrder);
  const formItemServerErrors = useFormItemServerErrors(newOrder.fieldErrors);
  const goToNewOrderSuccess = useHistoryAction('vydejNewOrderSuccess', { id: newOrder.id });

  const layers = useSelector((state) => state.mapCore.layers);
  const editMapFeatures = useMemo(
    () =>
      (layers.find((layer: LayerState) => layer.id === UNMANAGED_AREA_LAYERS.CUSTOM_AREAS) as VectorLayerState)
        ?.features ?? [],
    [layers],
  );
  const isNotSinglePolygonArea = useMemo(() => !isSinglePolygonArea(editMapFeatures), [editMapFeatures]);

  const userName = useSelector((state) => `${state.auth.name} ${state.auth.surname}`);
  const order_publishsets = newOrder.order_publishsets;
  const summaryPublishsets = useMemo(() => {
    return (
      order_publishsets.map((order_publishset) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
        const publishset = publishsetList.data?.results?.find(
          (publishset) => publishset.url === order_publishset.publishset,
        )!;
        const formats = order_publishset.required_formats.map(
          (formatUrl) => publishset?.formats.find((format) => format.url === formatUrl)!.name,
        );
        return {
          publishset,
          formats,
        };
      }) ?? []
    );
  }, [publishsetList, order_publishsets]);

  const [pricePreview, setPricePreview] = useState<OrderPricePreviewResponse | null>(null);
  const [isPricePreviewLoading, setIsPricePreviewLoading] = useState(false);
  const [isPricePreviewError, setIsPricePreviewError] = useState(false);

  const hasPricedPublishsets = useMemo(
    () => summaryPublishsets.some((item) => item.publishset?.price_list_item),
    [summaryPublishsets],
  );

  const priceRows = useMemo(
    (): PriceSummaryRow[] => [
      ...(pricePreview?.publishsets
        ?.filter((op) => !!op.price_list_item)
        .map((op) => ({
          key: op.publishset,
          name: op.publishset_name,
          area_ha: pricePreview.billed_area_ha,
          price_list_item_name: op.price_list_item_name,
          vat_rate: op.vat_rate,
          price_gross: op.price_gross,
        })) ?? []),
      ...createHandlingFeeRows(formatMessage, pricePreview?.handling_fee_net),
    ],
    [pricePreview, formatMessage],
  );

  const buildOrderRegions = useCallback((): OrderEditRequest['regions'] => {
    const result = [] as OrderEditRequest['regions'];
    let index = 0;
    for (const feature of editMapFeatures) {
      const geom = { ...feature.geometry } as ExportGeometry;
      geom.crs = { type: 'name', properties: { name: projection } };
      result.push({ geom, type: OrderRegionTypesEnum.CUSTOM, name: `custom${index}` });
      index++;
    }
    for (const city of newOrder.cities) result.push({ code: city.code ?? city.id, type: OrderRegionTypesEnum.OBEC });
    for (const orp of newOrder.orps) result.push({ code: orp.code ?? orp.id, type: OrderRegionTypesEnum.ORP });
    for (const cadaster of newOrder.cadasters) result.push({ code: cadaster.code ?? cadaster.id, type: OrderRegionTypesEnum.CADASTER });
    for (const region of newOrder.regions) result.push({ code: region.code ?? region.id, type: OrderRegionTypesEnum.KRAJ });
    for (const momc of newOrder.momcs) result.push({ code: momc.code ?? momc.id, type: OrderRegionTypesEnum.MOMC });
    return result;
  }, [editMapFeatures, newOrder.cities, newOrder.orps, newOrder.cadasters, newOrder.regions, newOrder.momcs, projection]);

  useEffect(() => {
    // Poplatek se platí i za data zdarma, objednávka s ním je placená.
    if ((!hasPricedPublishsets && !handling_fee) || !order_publishsets.length) {
      setPricePreview(null);
      setIsPricePreviewError(false);
      return;
    }
    const builtRegions = buildOrderRegions();
    if (!builtRegions.length) {
      setPricePreview(null);
      setIsPricePreviewError(false);
      return;
    }

    let cancelled = false;
    setIsPricePreviewLoading(true);
    setPricePreview(null);
    setIsPricePreviewError(false);

    ordersPricePreviewCreate({
      request: {
        order_publishsets,
        regions: builtRegions,
        order_type: from_primary_db ? OrderTypeEnum.STATE : order_type,
        selection_type: from_primary_db ? OrderSelectionTypesEnum.CUSTOM : selection_type,
        from_primary_db,
        is_public: from_primary_db ? true : is_public === ORDER_IS_PUBLIC.TRUE,
        handling_fee,
      },
    })
      .then((result) => {
        if (cancelled) return;
        if (result.ok && result.data) {
          setPricePreview(result.data);
        } else {
          setIsPricePreviewError(true);
        }
      })
      .finally(() => {
        if (!cancelled) setIsPricePreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [hasPricedPublishsets, handling_fee, order_publishsets, buildOrderRegions]);

  const agreement = useCheckbox<OmittedFormItemProps>({
    name: 'agreement',
    validate: newOrder.isRequirementAgreement,
    validator: (value) => (value ? null : formatMessage(newOrderMessages.agreementRequired)),
    onChange: useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(actionSetAgreement(e.target.checked));
      },
      [dispatch],
    ),
  });

  const handleChangeBtnClick = (e: React.MouseEvent, eventData: { data: NEW_ORDER_STEPS }) => {
    onStepChange(eventData.data);
  };

  const handleSubmitBtnClick = async () => {
    const regions = buildOrderRegions();

    const isRequirementPublishset = !order_publishsets.length;
    const isRequirementValidRegion = !regions.length || newOrder.isOverLimitArea || isNotSinglePolygonArea;
    const isRequirementAgreement = !newOrder.agreement;

    let isRequirementDate =
      newOrder.selection_type === OrderSelectionTypesEnum.PACKAGE &&
      newOrder.order_type === OrderTypeEnum.CHANGE &&
      !newOrder.order_data_changes_from;

    // validita datumu - pokud je nastaveno tak vetsi= nez min a mensi= nez max
    const isRequirementValidDateChangesFrom = !isDateValid(
      newOrder.order_data_changes_from,
      newOrder.dateValidityMin,
      newOrder.dateValidityMax,
    );
    const isRequirementValidDateStateAt = !isDateValid(
      newOrder.order_data_state_valid_at,
      newOrder.dateValidityMin,
      newOrder.dateValidityMax,
    );
    if (isRequirementValidDateChangesFrom || isRequirementValidDateStateAt) {
      isRequirementDate = true;
    }

    if (
      isRequirementPublishset ||
      isRequirementValidRegion ||
      isRequirementAgreement ||
      isRequirementDate
    ) {
      dispatch(
        actionSetRequirements(
          isRequirementPublishset,
          isRequirementValidRegion,
          isRequirementAgreement,
          isRequirementDate,
        ),
      );
    } else {
      const order = {
        attachments: newOrder.attachments as OrderEditAttachmentRequest[],
        ordering_reason: newOrder.reason,
        order_publishsets,
        regions,
        order_data_state_valid_at:
          order_data_state_valid_at && isOrderDataStateValid
            ? order_data_state_valid_at.toISOString()
            : null, // check for valid datetimestamp
        order_data_changes_from:
          order_data_changes_from && isOrderDataChangesFrom
            ? order_data_changes_from.toISOString()
            : null, // check for valid datetimestamp
        from_primary_db,
        order_type: from_primary_db ? OrderTypeEnum.STATE : order_type,
        is_public: from_primary_db ? true : is_public === ORDER_IS_PUBLIC.TRUE,
        selection_type: from_primary_db ? OrderSelectionTypesEnum.CUSTOM : selection_type,
        handling_fee,
        note: newOrder.note,
        origin: isCounterSectionVisible ? newOrder.origin : undefined,
      };

      const action = newOrder.id ? ordersPartialUpdate : ordersCreate;
      const response = await dispatch<any>(submitOrder(action, newOrder.id, order));
      if (response.ok) {
        goToNewOrderSuccess.replace({ id: response.data.id });
      }
    }
  };

  const isEmptyDate = useMemo(() => {
    return (
      newOrder.selection_type === OrderSelectionTypesEnum.PACKAGE &&
      newOrder.order_type === OrderTypeEnum.CHANGE &&
      !newOrder.order_data_changes_from
    );
  }, [newOrder.selection_type, newOrder.order_type, newOrder.order_data_changes_from]);

  return (
    <>
      <div className='sg-u-vs-2'>
        <section>
          <h4 className='sg-u-type-h2 sg-a-ml-2'>
            <FormattedMessage {...newOrderMessages.user} />
          </h4>
          <p className='sg-u-box sg-a-p-2 sg-a-mt-1'>{userName}</p>
        </section>
        <section>
          <h4 className='sg-u-type-h2 sg-a-ml-2'>
            {from_primary_db ? (
              <FormattedMessage {...newOrderMessages.publishSetsPrimary} />
            ) : (
              <FormattedMessage
                {...newOrderMessages.publishSets}
                values={{
                  customerType,
                  order_type,
                  show_order_data_state_valid_at: isOrderDataStateValid,
                  order_data_state_valid_at:
                    order_data_state_valid_at && isOrderDataStateValid
                      ? new Date(order_data_state_valid_at)
                      : null,
                  show_order_data_changes_from: isOrderDataChangesFrom,
                  order_data_changes_from:
                    order_data_changes_from && isOrderDataChangesFrom
                      ? new Date(order_data_changes_from)
                      : null,
                }}
              />
            )}
          </h4>

          <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-a-d-f sg-a-ai-b sg-u-hsl-2'>
            <div className='sg-a-f-a'>
              <div className='sg-a-mb-1'>
                {newOrder.isRequirementDate ? (
                  <DangerAlert>
                    <FormattedMessage {...newOrderMessages.noDate} />
                  </DangerAlert>
                ) : isEmptyDate ? (
                  <InfoAlert>
                    <FormattedMessage {...newOrderMessages.emptyDate} />
                  </InfoAlert>
                ) : null}
              </div>

              {summaryPublishsets.length > 0 ? (
                <ul className='sg-u-vs-1'>
                  {summaryPublishsets.map((publishset) => (
                    <li key={publishset.publishset?.url}>
                      <PublishsetSummaryItem
                        name={publishset.publishset?.name}
                        needsApproval={publishset.publishset?.needs_approval}
                        formats={publishset.formats}
                        priceListItem={publishset.publishset?.price_list_item}
                      />
                    </li>
                  ))}
                </ul>
              ) : newOrder.isRequirementPublishset ? (
                <DangerAlert>
                  <FormattedMessage {...newOrderMessages.noPublishSet} />
                </DangerAlert>
              ) : (
                <InfoAlert>
                  <FormattedMessage {...newOrderMessages.emptyPublishSet} />
                </InfoAlert>
              )}
            </div>
            <div className='sg-a-f-n'>
              <SecondaryBtn
                data={NEW_ORDER_STEPS.PUBLISH_SETS}
                size="sml"
                onClick={handleChangeBtnClick}
              >
                <FormattedMessage {...newOrderMessages.updateBtn} />
              </SecondaryBtn>
            </div>
          </div>
        </section>
        <section>
          <h4 className='sg-u-type-h2 sg-a-ml-2'>
            <FormattedMessage {...newOrderMessages.area} />
          </h4>
          <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-a-d-f sg-a-ai-b sg-u-hsl-2'>
            <div className='sg-a-f-a sg-u-vs-2'>
              <AreasSummary
                cities={newOrder.cities}
                cadasters={newOrder.cadasters}
                orps={newOrder.orps}
                momcs={newOrder.momcs}
                regions={newOrder.regions}
                customAreasCount={editMapFeatures.length}
              />
              {newOrder.cities.length < 1 &&
                newOrder.cadasters.length < 1 &&
                newOrder.orps.length < 1 &&
                newOrder.momcs.length < 1 &&
                newOrder.regions.length < 1 &&
                editMapFeatures.length < 1 &&
                !newOrder.isRequirementValidRegion && (
                  <InfoAlert>
                    <FormattedMessage {...newOrderMessages.emptyArea} />
                  </InfoAlert>
                )}
              {newOrder.isRequirementValidRegion && (
                <DangerAlert>
                  {newOrder.isOverLimitArea ? (
                    <FormattedMessage {...newOrderMessages.overLimitArea} />
                  ) : isNotSinglePolygonArea ? (
                    <FormattedMessage {...newOrderMessages.notSinglePolygonArea} />
                  ) : (
                    <FormattedMessage {...newOrderMessages.noArea} />
                  )}
                </DangerAlert>
              )}
            </div>
            <div className='sg-a-f-n'>
              <SecondaryBtn
                data={NEW_ORDER_STEPS.REGIONS}
                size="sml"
                onClick={handleChangeBtnClick}
              >
                <FormattedMessage {...newOrderMessages.updateBtn} />
              </SecondaryBtn>
            </div>
          </div>
        </section>
        {newOrder.reason && (
          <section>
            <h4 className='sg-u-type-h2 sg-a-ml-2'>
              <FormattedMessage {...newOrderMessages.reason} />
            </h4>
            <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-a-d-f sg-a-ai-b sg-u-hsl-2'>
              <p className='sg-a-f-a sg-u-type-italic'>{newOrder.reason}</p>
              <div className='sg-a-f-n'>
                <SecondaryBtn
                  data={NEW_ORDER_STEPS.REASON}
                  size="sml"
                  onClick={handleChangeBtnClick}
                >
                  <FormattedMessage {...newOrderMessages.updateBtn} />
                </SecondaryBtn>
              </div>
            </div>
          </section>
        )}
        {isCounterSectionVisible && newOrder.origin && (
          <section>
            <h4 className='sg-u-type-h2 sg-a-ml-2'>
              <FormattedMessage {...newOrderMessages.originLabel} />
            </h4>
            <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-a-d-f sg-a-ai-b sg-u-hsl-2'>
              <p className='sg-a-f-a'>{formatMessage.lookup(originMessages, newOrder.origin)}</p>
              <div className='sg-a-f-n'>
                <SecondaryBtn
                  data={NEW_ORDER_STEPS.REASON}
                  size='sml'
                  onClick={handleChangeBtnClick}
                >
                  <FormattedMessage {...newOrderMessages.updateBtn} />
                </SecondaryBtn>
              </div>
            </div>
          </section>
        )}
        {(newOrder.note || formItemServerErrors.note) && (
          <section>
            <h4 className='sg-u-type-h2 sg-a-ml-2'>
              <FormattedMessage {...newOrderMessages.note} />
            </h4>
            <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-a-d-f sg-a-ai-b sg-u-hsl-2'>
              <div className='sg-a-f-a sg-u-vs-1'>
                {formItemServerErrors.note && (
                  <DangerAlert>{formItemServerErrors.note.errorMessage}</DangerAlert>
                )}
                <p className='sg-a-ws-pw'>{newOrder.note}</p>
              </div>
              <div className='sg-a-f-n'>
                <SecondaryBtn
                  data={NEW_ORDER_STEPS.REASON}
                  size='sml'
                  onClick={handleChangeBtnClick}
                >
                  <FormattedMessage {...newOrderMessages.updateBtn} />
                </SecondaryBtn>
              </div>
            </div>
          </section>
        )}
        {newOrder.attachments && newOrder.attachments.length > 0 && (
          <section>
            <h4 className='sg-u-type-h2 sg-a-ml-2'>
              <FormattedMessage {...newOrderMessages.attachments} />
            </h4>
            <div className='sg-u-box sg-a-p-2 sg-a-mt-1 sg-a-d-f sg-a-ai-b sg-u-hsl-2'>
              <div className='sg-a-f-a sg-u-vs-1'>
                {formItemServerErrors.file && (
                  <DangerAlert>{formItemServerErrors.file.errorMessage}</DangerAlert>
                )}
                <ul>
                  {newOrder.attachments.map((attachment) => (
                    <li key={attachment.luid || attachment.id}>{attachment.name}</li>
                  ))}
                </ul>
              </div>
              <div className='sg-a-f-n'>
                <SecondaryBtn
                  data={NEW_ORDER_STEPS.REASON}
                  size="sml"
                  onClick={handleChangeBtnClick}
                >
                  <FormattedMessage {...newOrderMessages.updateBtn} />
                </SecondaryBtn>
              </div>
            </div>
          </section>
        )}

        {isPricePreviewError && (
          <DangerAlert>
            <FormattedMessage {...orderPriceMessages.previewError} />
          </DangerAlert>
        )}
        {(isPricePreviewLoading || pricePreview !== null) && (
          <PriceSummary
            rows={priceRows}
            total_gross={pricePreview?.total_gross}
            total_net={pricePreview?.total_net}
            total_vat={pricePreview?.total_vat}
            isLoading={isPricePreviewLoading}
            isPreview
          />
        )}

        <FormItem {...agreement}>
          <Checkbox {...agreement}>
            <>
              <FormattedMessage {...newOrderMessages.agreement} />
              {config.agreement && (
                <Link href={config.agreement} severity='colorful' target='_blank' isExternal>
                  {formatMessage(newOrderMessages.agreementLink)}
                </Link>
              )}
            </>
          </Checkbox>
        </FormItem>

        {newOrder.queryState === QUERY_STATE.WAITING && (
          <WaitingAlert>
            <FormattedMessage {...newOrderMessages.queryStateWaiting} />
          </WaitingAlert>
        )}

        {newOrder.queryState === QUERY_STATE.FAIL &&
          !formItemServerErrors.file &&
          !formItemServerErrors.note && (
            <DangerAlert>
              {formItemServerErrors.non_field_errors?.errorMessage || (
                <FormattedMessage {...newOrderMessages.queryStateFail} />
              )}
            </DangerAlert>
          )}
      </div>

      <StickyFooter>
        <BtnBar>
          <CtaBtn onClick={handleSubmitBtnClick}>
            <FormattedMessage {...newOrderMessages.submitBtn} />
          </CtaBtn>
          <BtnBarSpan />
          <SecondaryBtn
            icon={{ position: 'left', element: <SvgArrowLeft /> }}
            onClick={onPrevStepClick}
          >
            {config.hideReason ? (
              <FormattedMessage {...newOrderMessages.selectRegionPrevBtnLabel} />
            ) : customerType === 'ipr' ? (
              <FormattedMessage {...newOrderMessages.detailsPrevBtnLabel} />
            ) : (
              <FormattedMessage {...newOrderMessages.reasonPrevBtnLabel} />
            )}
          </SecondaryBtn>
        </BtnBar>
      </StickyFooter>
    </>
  );
};
